import React, { useState } from "react"
import { Link } from "react-router-dom"
import { useNavigateToPost } from "../../hooks/useNavigateToPost"
import { SmartDateFormat } from "../../util/dates"
import { PauseCircle, PlayCircle, PlusCircle } from "react-bootstrap-icons"
import { Spinner } from "../shared/Spinner"
import { RequestError } from "../shared/RequestError"
import { compareByKey } from "../../util/utils"
import CommentSectionPreview from "../comments/CommentSectionPreview"
import { useDispatch, useSelector } from "react-redux"
import {
  prependAndPlay,
  pushToQueue,
  playOrPause,
} from "../../stores/mediaPlayerSlice"
import { SingleTrackItem } from "./SingleTrackItem"
import { PostListItemStyles } from "./constants"
import { usePost } from "../../hooks/usePost"
import { ShareUrlButton } from "../ShareUrlButton"
import { toast } from "react-toastify"
import { AnchorButton, ShowHideButton } from "../shared/Button"
import { ReactionBlock } from "./ReactionBlock"
import RelatedPosts from "../RelatedPosts"
import clsx from "clsx"
import PremiereHeader from "./PremiereHeader"
import styles from "./styles.module.css"

const ICON_SIZE = "1.6rem"
const MARGIN_SPACING = "0rem 0.5rem 0rem 0"

export const AlbumListItem = ({ album, updateStatus, adminMode = false }) => {
  const [postStatus, setPostStatus] = useState(album.status)
  const albumDetailData = usePost(album.id, "songs")
  var albumDetails = albumDetailData?.data?.post
  const navigateToPostScreen = useNavigateToPost(album.nada_path)

  const dispatch = useDispatch()
  const currentMediaItem = useSelector(
    // @ts-ignore
    (state) => state.mediaPlayer.value.currentMediaItem,
  )

  // @ts-ignore
  const playerState = useSelector(
    (state) => state.mediaPlayer.value.playerState,
  )
  const isCurrentlyPlaying =
    playerState === "playing" && currentMediaItem?.albumId === album.id
  const isCurrentlyQueued =
    playerState === "paused" && currentMediaItem?.albumId === album.id

  const onPlayClick = (e) => {
    dispatch(prependAndPlay(sortedSongs()))
  }
  const onPlayOrPauseClick = (e) => {
    dispatch(playOrPause())
  }

  const onAddClick = (e) => {
    dispatch(pushToQueue(sortedSongs()))
    toast.success(`${album.title} added to end of Queue`, {
      position: "top-center",
      autoClose: 2000,
      hideProgressBar: true,
      closeOnClick: true,
    })
  }

  const sortedSongs = () => {
    return [...albumDetails.songs]
      .sort((a, b) => compareByKey(a, b, "track_number"))
      .map((song) => ({
        ...song,
        album_name: albumDetails.title,
        cover_image_small: albumDetails.cover_image_small,
      }))
  }

  return (
    <div className="grid grid-cols-12">
      <div className={PostListItemStyles.contentDiv}>
        <img
          src={album.external_photo_src}
          alt={album.title}
          className="w-full cursor-pointer"
          style={{ aspectRatio: "1/1" }}
          onClick={navigateToPostScreen}
        />
      </div>
      <div className={PostListItemStyles.metaDiv}>
        <PremiereHeader post={album} size="small" />
        <div className="flex">
          <div className="flex flex-none">
            {isCurrentlyPlaying ? (
              <PauseCircle
                title="Play Now"
                className="cursor-pointer"
                style={{
                  minWidth: ICON_SIZE,
                  width: ICON_SIZE,
                  height: ICON_SIZE,
                  margin: MARGIN_SPACING,
                }}
                onClick={onPlayOrPauseClick}
              />
            ) : (
              <PlayCircle
                title="Play Now"
                className="cursor-pointer"
                style={{
                  minWidth: ICON_SIZE,
                  width: ICON_SIZE,
                  height: ICON_SIZE,
                  margin: MARGIN_SPACING,
                }}
                onClick={isCurrentlyQueued ? onPlayOrPauseClick : onPlayClick}
              />
            )}

            <PlusCircle
              title="Add to Queue"
              className="cursor-pointer"
              style={{
                minWidth: ICON_SIZE,
                width: ICON_SIZE,
                height: ICON_SIZE,
                margin: MARGIN_SPACING,
              }}
              onClick={onAddClick}
            />
          </div>

          <div className="flex items-start justify-between flex-grow">
            <h2
              className={clsx(
                PostListItemStyles.header,
                "inline mr-2 cursor-pointer",
                album.is_bn_premiere && styles.premiereTitle,
              )}
              dangerouslySetInnerHTML={{ __html: album.title }}
              onClick={navigateToPostScreen}
            ></h2>
            <ShareUrlButton nadaUrl={album.nada_url} renderMode="icon" />
          </div>
        </div>
        <h3>
          <Link className={PostListItemStyles.bandName} to={album.local_path}>
            {album.band_name}
          </Link>
        </h3>
        <div className="text-xs">
          {!album.is_released ? (
            <div>
              Announced&nbsp;
              {SmartDateFormat({
                dateStr: album.announced_at,
                includeTime: false,
              })}
              &nbsp;for Release on&nbsp;
              {SmartDateFormat({
                dateStr: album.published_at,
                includeTime: false,
              })}
            </div>
          ) : (
            <div>
              Released&nbsp;
              {SmartDateFormat({
                dateStr: album.published_at,
                includeTime: false,
              })}
            </div>
          )}
        </div>
        <div className={PostListItemStyles.externalLink}>
          {album.class_name === "Bandcamp" ? (
            <div className="mt-1">
              <AnchorButton href={album.external_url} target="_blank">
                Buy on&nbsp;
                {album.class_name}
              </AnchorButton>
            </div>
          ) : album.class_name === "BandNada" &&
            album.bn_exclusive_until &&
            new Date(album.bn_exclusive_until) > new Date() ? (
            <span className="font-bold text-bn-premiere-green">
              exclusively on bandNada
            </span>
          ) : (
            <a href={album.external_url} target="_blank" rel="noreferrer">
              view on&nbsp;
              {album.class_name}
            </a>
          )}
        </div>
        <div className="mt-2">
          {albumDetailData.status === "loading" ? (
            <Spinner className="text-gray-500" />
          ) : albumDetailData.status === "error" ? (
            <RequestError error={albumDetailData.error} />
          ) : (
            <>
              {albumDetails.songs?.length > 0 ? (
                <>
                  <ul className="text-sm text-black">
                    {sortedSongs().map((song, index) => (
                      <SingleTrackItem
                        key={song.id}
                        song={song}
                        index={index}
                      />
                    ))}
                  </ul>
                </>
              ) : null}
            </>
          )}
        </div>

        <div className="mt-4">
          <RelatedPosts postId={album.id} />
        </div>

        {adminMode && (
          <ShowHideButton
            updateStatus={updateStatus}
            postStatus={postStatus}
            setPostStatus={setPostStatus}
          />
        )}
        <div className="mt-6">
          <ReactionBlock
            contentId={album.id}
            contentType="Post"
            postId={album.id}
          />
        </div>
        <CommentSectionPreview
          postId={album.id}
          postPath={album.nada_url}
        />
      </div>
    </div>
  )
}
